import { getFromDataPath, reduceDataPath } from "../../utility";
import { OperationRoleEnum } from "../ars_ldm";
import { useArsContext } from "../contexts";
import { ArsSelect } from "../inputs/ArsSelect";
import { ArsText } from "../inputs/ArsText";
import { isAnalysisMethod, isOperation } from "../typeGuards";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the ReferencedOperationRelationshipControl component.
 */
export interface ReferencedOperationRelationshipControlProps {
  /** RFC 6901 JSON Pointer path to the ReferencedOperationRelationship in ArsContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Sibling count */
  siblingCount: number;
}

/**
 * A component for managing ReferencedOperationRelationship entities.
 *
 * This component provides:
 * - Role selection via OperationRoleEnum
 * - Operation and analysis identifier inputs
 * - Description input
 * - Remove button for deleting the relationship
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the ReferencedOperationRelationship in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.siblingCount - Number of sibling items
 * @returns A React functional component rendering the referenced operation relationship editor, or null if the analysis method or operation is not found in state.
 */
export const ReferencedOperationRelationshipControl = ({
  dataPath,
  disabled = false,
  siblingCount,
}: ReferencedOperationRelationshipControlProps): React.ReactNode => {
  const { state } = useArsContext();
  const roleOptions = Object.values(OperationRoleEnum).map((role) => ({
    value: role,
    label: role,
  }));

  const idRoute = dataPath.replace(/\//g, "-");
  const thisAnalysisMethod = getFromDataPath(reduceDataPath(dataPath, 4), state, isAnalysisMethod);
  const thisOperation = getFromDataPath(reduceDataPath(dataPath, 2), state, isOperation);
  if (!thisAnalysisMethod || !thisOperation) return null;

  const otherOperationNames = thisAnalysisMethod.operations
    .map((op) => ({ value: op.id, label: op.name }))
    .filter((op) => op.label !== thisOperation.name);

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute top-0 end-0 m-2">
        <RemoveArsItemButton
          dataPath={dataPath}
          disabled={disabled}
          siblingCount={siblingCount}
        />
      </div>

      <ArsSelect
        dataPath={`${dataPath}/referencedOperationRole/controlledTerm`}
        id={`${idRoute}-role`}
        label="Role"
        labelWidth={2}
        options={roleOptions}
        disabled={disabled}
      />

      <ArsSelect
        dataPath={`${dataPath}/operationId`}
        id={`${idRoute}-operation-id`}
        label="Operation name"
        labelWidth={2}
        options={otherOperationNames}
        disabled={disabled}
      />

      <ArsText
        dataPath={`${dataPath}/description`}
        id={`${idRoute}-description`}
        label="Description"
        labelWidth={2}
        placeholder="Enter description"
        type="textarea"
        debounceMilliseconds={2000}
        rows={3}
        disabled={disabled}
      />
    </div>
  );
};

ReferencedOperationRelationshipControl.displayName = "ReferencedOperationRelationshipControl";
